import { useCallback, useState } from 'react';
import Papa from 'papaparse';
import { toast } from 'sonner';
import { apiGet, operationsClient } from '@/lib/api';
import { timesheetEndpoints } from '@/lib/api/endpoints';
import type { TimesheetExportResponse } from '@/types/timesheet';

// Earnings codes for the wide export. The configurable ST-pay-type → payroll
// processor (Gusto/ADP/QBO) mapping is a follow-up.
const EARNINGS_CODES = 'REG/OT/TRV';

function money(value: number | null): string {
  return value != null ? value.toFixed(2) : '';
}

export function useTimesheetExport() {
  const [isExporting, setIsExporting] = useState(false);

  const exportCSV = useCallback(
    async (params: { periodStart: string; periodEnd: string; approvedOnly: boolean }) => {
      setIsExporting(true);
      try {
        // $ amounts are computed server-side from each technician's payroll
        // profile (server-authoritative); the FE only formats the CSV.
        const data = await apiGet<TimesheetExportResponse>(
          operationsClient,
          timesheetEndpoints.export(),
          {
            params: {
              periodStart: params.periodStart,
              periodEnd: params.periodEnd,
              approvedOnly: params.approvedOnly ? 'true' : 'false',
            },
          },
        );

        if (data.rows.length === 0) {
          toast.error(
            params.approvedOnly ? 'No approved timesheets to export' : 'No timesheets to export',
          );
          return;
        }

        // The purchase-order columns come only where purchasing is on: the
        // export leaves them out otherwise (PLAT-FF-1).
        const charges = data.rows.some((r) => r.purchaseCharges !== undefined);
        const csvRows = data.rows.map((r) => ({
          Technician: r.technicianName,
          'Technician ID': r.technicianId,
          'Period start': data.periodStart,
          'Period end': data.periodEnd,
          'Regular hours': r.regularHours.toFixed(2),
          'Overtime hours': r.overtimeHours.toFixed(2),
          'Travel hours': r.travelHours.toFixed(2),
          'Total hours': r.totalHours.toFixed(2),
          'Regular $': money(r.regularPay),
          'Overtime $': money(r.overtimePay),
          'Travel $': money(r.travelPay),
          Commission: money(r.commission),
          Gross: money(r.gross),
          ...(charges
            ? {
                'Purchase orders charged': money(r.purchaseCharges ?? 0),
                Net: money(r.net ?? null),
              }
            : {}),
          'Earnings codes': EARNINGS_CODES,
          'Approval status': r.approvalStatus,
        }));

        const csv = Papa.unparse(csvRows);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `timesheet_${data.periodStart}_${data.periodEnd}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        if (data.withoutProfile > 0) {
          toast.warning(
            `${data.withoutProfile} technician${data.withoutProfile === 1 ? '' : 's'} without a payroll profile — $ left blank`,
          );
        }
      } catch {
        toast.error('Failed to export payroll');
      } finally {
        setIsExporting(false);
      }
    },
    [],
  );

  return { exportCSV, isExporting };
}
